import { useState } from 'react';
import { StoryConfig, EnrichedStory } from '../types/StoryConfig';
import { generateStory, updateStoryVisibility } from '../services/storyService';
import { useAuth } from '../context/AuthContext';
import { AgeRange } from '../types/Story';

interface StoryGeneratorState {
  loading: boolean;
  error: string | null;
  generatedStory: EnrichedStory | null;
  savedStoryId: string | null;
  progress: number; // 0-100
  creditDeducted: boolean; // Add this to track if credit was deducted
  generationAttempts: number; // Add this to track number of attempts
}

interface StoryGeneratorHook extends StoryGeneratorState {
  generateNewStory: (config: StoryConfig) => Promise<boolean>;
  saveStory: (visibility?: 'public' | 'private') => Promise<string | null>;
  resetGenerator: () => void;
}

export const useStoryGenerator = (): StoryGeneratorHook => {
  const { user } = useAuth();
  const [state, setState] = useState<StoryGeneratorState>({
    loading: false,
    error: null,
    generatedStory: null,
    savedStoryId: null,
    progress: 0,
    creditDeducted: false, // Initialize with false
    generationAttempts: 0 // Initialize with 0
  });

  const generateNewStory = async (config: StoryConfig): Promise<boolean> => {
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      progress: 10,
      creditDeducted: false, // Reset to default
      generationAttempts: 0 // Reset to default 
    }));
    
    try {
      // Step 1: Generate the story text
      const storyResponse = await generateStory(config, user!.id);
      
      if (!storyResponse.success || !storyResponse.data) {
        setState(prev => ({ 
          ...prev, 
          loading: false, 
          error: storyResponse.error || 'Failed to generate story', 
          progress: 0,
          // Include credit information if available
          creditDeducted: storyResponse.data?.creditDeducted ?? false,
          generationAttempts: storyResponse.data?.generationAttempts ?? 0
        }));
        
        // Throw the error to allow component-level handling
        throw new Error(storyResponse.error || 'Failed to generate story');
      }
      
      setState(prev => ({ ...prev, progress: 60 }));

      setState(prev => ({
        ...prev,
        loading: false,
        generatedStory: {
          imagePrompt: storyResponse.data!.imagePrompt,
          story: {
            ...storyResponse.data!.story,
            ageRange: config.childAge,
            storyType: config.storyType,
            createdBy: user?.id ?? 'anonymous',
            language: config.language || 'en'
          },
          imageUrls: storyResponse.data!.story.imageUrls
        },
        /*
         * Generation persists the story as private, so it already has a
         * real id — recorded here so read counts, likes and visibility can
         * act on it before the user presses Save.
         */
        savedStoryId: storyResponse.data!.story.id ?? null,
        progress: 100,
        // Include the new fields from backend with default values if missing
        creditDeducted: storyResponse.data!.creditDeducted ?? false,
        generationAttempts: storyResponse.data!.generationAttempts ?? 0
      }));

      return true;
    } catch (err: any) {
      const errorMsg = 
        err.response?.data?.error || 
        err.message || 
        'An unexpected error occurred during story generation';
        
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMsg, 
        progress: 0,
        // Include credit info if available in the error response
        creditDeducted: err.response?.data?.creditDeducted ?? false,
        generationAttempts: err.response?.data?.generationAttempts ?? 0
      }));
      
      // Re-throw to allow component handling
      throw err;
    }
  };

  /*
   * The story is already saved (privately) by the time it is generated, so
   * this no longer creates anything — creating here would duplicate it.
   * Publishing is the only thing left to do, and only when the user asked
   * for public; private is already the stored state.
   */
  const saveStory = async (visibility: 'public' | 'private' = 'public'): Promise<string | null> => {
    const storyId = state.savedStoryId;

    if (!state.generatedStory || !user || !storyId) {
      setState(prev => ({
        ...prev,
        error: 'No story to save or user not logged in'
      }));
      return null;
    }

    if (visibility === 'private') {
      return storyId;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await updateStoryVisibility(storyId, user.id, 'public');

      if (response.success) {
        setState(prev => ({ ...prev, loading: false }));
        return storyId;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.error || 'Failed to publish story'
      }));
      return null;
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'An unexpected error occurred while publishing the story'
      }));
      return null;
    }
  };

  const resetGenerator = () => {
    setState({
      loading: false,
      error: null,
      generatedStory: null,
      savedStoryId: null,
      progress: 0,
      creditDeducted: false, // Reset to default false
      generationAttempts: 0 // Reset to default 0
    });
  };

  return {
    ...state,
    generateNewStory,
    saveStory,
    resetGenerator,
  };
};

export function ageRangeValueOf(value: string): AgeRange | undefined {
  if (Object.values(AgeRange).includes(value as AgeRange)) {
    return value as AgeRange;
  }
  return undefined;
}